import {
  format,
  parseISO,
  compareAsc,
  compareDesc,
  subDays,
  formatDistanceToNowStrict,
  differenceInSeconds,
  isValid,
} from 'date-fns';

/**
 * ISO date string (`2021-10-15T15:42:43.083Z`)
 */
export type DateIsoString =
  `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

export const now = (): string => new Date().toISOString();

/**
 * Formats a date as a human-readable string.
 *
 * Options:
 * - If `relative` is true, returns a relative time string like:
 *   - 'just now' (within 60 seconds)
 *   - '4 days ago', 'in 2 hours', etc.
 * - If `relative` is false or omitted:
 *   - Dates from the current year are formatted as 'MMM dd' (e.g., 'Apr 16')
 *   - Dates from other years are formatted as 'MMM dd, yyyy' (e.g., 'Oct 24, 2024')
 *   - If `includeTime` is true, the time is appended (e.g., 'Apr 16 at 03:45 PM')
 *
 * If `prefix` is provided, it will be prepended to the result (e.g., 'Updated Apr 16').
 *
 * If the provided date is invalid, logs an error and returns null.
 *
 * @param date - The date to format
 * @param relative - Whether to format the date as a relative time string
 * @param includeTime - Whether to include time in the formatted date
 * @param prefix - Optional prefix to prepend to the output string
 *
 * @example
 * formatDate({ date: new Date('2025-04-16') }) // 'Apr 16'
 * formatDate({ date: new Date('2024-10-24') }) // 'Oct 24, 2024'
 * formatDate({ date: new Date(), relative: true }) // 'just now'
 * formatDate({ date: new Date(), prefix: 'Updated', relative: true }) // 'Updated just now'
 */
export function formatDate({
  date,
  relative,
  includeTime,
  prefix,
}: {
  date: Date;
  relative?: boolean;
  includeTime?: boolean;
  prefix?: string;
}): string | null {
  if (!isValid(date)) {
    console.error('formatDate: Invalid date provided:', date);
    return null;
  }

  let dateFormat: string;
  let output: string;

  if (relative) {
    const secondsDiff = Math.abs(differenceInSeconds(new Date(), date));
    output =
      secondsDiff < 60
        ? 'just now'
        : formatDistanceToNowStrict(date, { addSuffix: true });
  } else {
    const isCurrentYear = date.getFullYear() === new Date().getFullYear();
    if (includeTime) {
      dateFormat = isCurrentYear
        ? `MMM dd 'at' hh:mm aaa`
        : `MMM dd, yyyy 'at' hh:mm aaa`;
    } else {
      dateFormat = isCurrentYear ? 'MMM dd' : 'MMM dd, yyyy';
    }
    output = format(date, dateFormat);
  }

  return prefix ? `${prefix} ${output}` : output;
}

/** Get the ISO date string (`2021-10-15T15:42:43.083Z`) for right now */
export const nowInIsoDate = (): string => new Date().toISOString();

export const yesterdayInIsoDate = (): string =>
  subDays(new Date(), 1).toISOString();

export function sortDateAsc(a: { date: string }, b: { date: string }): number {
  return compareAsc(parseISO(a.date), parseISO(b.date));
}

export function sortDateDesc(a: { date: string }, b: { date: string }): number {
  return compareDesc(parseISO(a.date), parseISO(b.date));
}

export const minutesFromNowInIsoDate = (minutes: number): string => {
  return new Date(Date.now() + minutes * 60 * 1000).toISOString();
};

/**
 * Converts hours, minutes, and seconds to milliseconds
 * @param h - hours (optional)
 * @param m - minutes (optional)
 * @param s - seconds (optional)
 * @returns total milliseconds
 */
export function toMilliseconds({
  hours = 0,
  minutes = 0,
  seconds = 0,
}: {
  hours?: number;
  minutes?: number;
  seconds?: number;
}) {
  const MS_PER_SECOND = 1000;
  const MS_PER_MINUTE = 60 * MS_PER_SECOND;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;

  return (
    hours * MS_PER_HOUR + minutes * MS_PER_MINUTE + seconds * MS_PER_SECOND
  );
}
